import {
  ClockCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
  WarningFilled,
} from '@ant-design/icons';

/**
 * Oversight visual language. Every severity / breach signal pairs COLOUR with a GLYPH and TEXT so it
 * survives deuteranopia and grayscale (WCAG 2.2 AA). Severity tokens are tenant-defined; we map the
 * common ones to an intent and render the raw token verbatim.
 */

export type OversightIntent = 'critical' | 'high' | 'medium' | 'low' | 'info' | 'neutral';

const INTENT_VAR: Record<OversightIntent, string> = {
  critical: 'var(--ol-negative)',
  high: 'var(--ol-negative)',
  medium: 'var(--ol-attention)',
  low: 'var(--ol-info)',
  info: 'var(--ol-info)',
  neutral: 'var(--ol-text-tertiary)',
};

export function severityIntent(severity: string): OversightIntent {
  switch (severity?.toUpperCase?.()) {
    case 'CRITICAL':
      return 'critical';
    case 'HIGH':
      return 'high';
    case 'MEDIUM':
    case 'MED':
      return 'medium';
    case 'LOW':
      return 'low';
    case 'INFO':
    case 'INFORMATIONAL':
      return 'info';
    default:
      return 'neutral';
  }
}

export function severityColor(severity: string): string {
  return INTENT_VAR[severityIntent(severity)];
}

/** A severity pill — colour + glyph + the verbatim token. */
export function SeverityTag({ severity }: { severity: string }) {
  const intent = severityIntent(severity);
  const color = INTENT_VAR[intent];
  const Icon =
    intent === 'critical' || intent === 'high'
      ? ExclamationCircleFilled
      : intent === 'medium'
        ? WarningFilled
        : InfoCircleFilled;
  return (
    <span
      title={severity}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        color,
        fontSize: 11,
        fontWeight: 700,
        padding: '1px 8px',
        borderRadius: 999,
        border: '1px solid currentColor',
        background: 'color-mix(in srgb, currentColor 10%, transparent)',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon aria-hidden />
      {severity || 'UNKNOWN'}
    </span>
  );
}

const ALERT_TYPE_META: Record<string, { label: string; icon: React.ReactNode }> = {
  SLA_BREACH: { label: 'SLA breach', icon: <ClockCircleFilled aria-hidden /> },
  RECONCILIATION_BREAK: { label: 'Reconciliation break', icon: <WarningFilled aria-hidden /> },
  REVIEW_OVERDUE: { label: 'Review overdue', icon: <ExclamationCircleFilled aria-hidden /> },
};

export function alertTypeLabel(type: string): string {
  return ALERT_TYPE_META[type]?.label ?? type;
}
export function alertTypeIcon(type: string): React.ReactNode {
  return ALERT_TYPE_META[type]?.icon ?? <InfoCircleFilled aria-hidden />;
}

/**
 * A numeric oversight metric cell. `breach` metrics (SLA breaches, recon breaks, unresolved signals)
 * turn attention/negative when non-zero and carry a warning glyph; benign counts (entities, cases)
 * render calm. Always tabular-nums for column scanning.
 *
 * NULL-HONESTY: `null`/`undefined` means "no source wired in this deployment" (the backend's
 * honesty contract) and renders as an em-dash with an explanatory tooltip — NEVER a fabricated 0.
 */
export function MetricCell({
  value,
  kind = 'benign',
}: {
  value: number | null | undefined;
  kind?: 'benign' | 'breach' | 'signal';
}) {
  if (value == null) {
    return (
      <span
        title="No source wired for this metric in this deployment — see the data-sources panel."
        aria-label="No source wired for this metric"
        style={{ color: 'var(--ol-text-tertiary)', fontSize: 13, fontWeight: 600 }}
      >
        —
      </span>
    );
  }
  const nonZero = value > 0;
  let color = 'var(--ol-text-primary)';
  let glyph: React.ReactNode = null;
  if (kind === 'breach' && nonZero) {
    color = 'var(--ol-negative)';
    glyph = <ExclamationCircleFilled aria-hidden style={{ fontSize: 11 }} />;
  } else if (kind === 'signal' && nonZero) {
    color = 'var(--ol-attention)';
    glyph = <WarningFilled aria-hidden style={{ fontSize: 11 }} />;
  } else if (!nonZero) {
    color = 'var(--ol-text-tertiary)';
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        color,
        fontWeight: nonZero && kind !== 'benign' ? 700 : 600,
        fontVariantNumeric: 'tabular-nums',
        fontSize: 13,
      }}
    >
      {glyph}
      {value.toLocaleString()}
    </span>
  );
}
